import React, { FC, useRef } from 'react';
import { AuthFlowAction } from '@/components/blocks/AuthentificationFlow';
import { Divider, Drawer, IconButton, Paper, Stack, Typography } from '@mui/material';
import { DrawerRegisterContentSx } from '@/components/blocks/DrawerRegister/styles/main';
import { Register } from '@/components/content/Register';
import {
	DrawerLoginFrameSx,
	DrawerLoginTitleSx,
} from '@/components/content/Cart/parts/DrawerLogin/styles/main';
import { useSWRConfig } from 'swr';
import {
	LOGIN_STEPS,
	personMutatorKeyMatcher,
	useLoginLess,
} from '@/data/Content/Login';
import { usePasswordExpiredRedirect } from '@/data/Content/hooks/usePasswordExpiredRedirect';
import { useLocalization } from '@/data/Localization';
import {
	createAccountLabelSX,
} from '@/components/content/Login/styles/button';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useSuccessLoginNotify } from '@/utils/useSuccessLoginNotify';
import { useReplaceAuthRoute } from 'integration/hooks/useReplaceAuthRoute';
import { SingleLoginLessForm } from '@/components/blocks/SingleLoginLessForm';
import { SingleLoginLessTitle } from '@/components/blocks/SingleLoginLessTitle';
import { RegistrationOrigin } from '@/data/Content/Registration';
import {
	ModalLoginHeaderIconCloseFrameSx,
	ModalLoginHeaderIconCloseSx,
} from '@/components/blocks/ModalLogin/styles/main';
import { Z_INDEX } from '@/styles/zindex';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';

type AuthFlowDrawerProps = {
	action: AuthFlowAction;
	open: boolean;
	origin?: RegistrationOrigin;
	onClose: () => void;
	onGo: (action: AuthFlowAction) => void;
	onAuthSuccess?: () => void;
};

export const AuthFlowDrawer: FC<AuthFlowDrawerProps> = ({
	action,
	open,
	origin,
	onClose,
	onGo,
	onAuthSuccess,
}) => {
	const { mutate } = useSWRConfig();
	const signInNLS = useLocalization('SignIn');
	const noAccountMsg = signInNLS.noAccount.t();
	const sigIn = signInNLS.registerNow.t();
	const { showSuccessAuthLogin } = useSuccessLoginNotify();
	const { onReplaceAuthRoute } = useReplaceAuthRoute({ shouldCallToModal: true, origin });

	const {
		stateLogin,
		baseSettings,
		singleSettingsLoading,
		nextRouteLoading,
		onChangeLogonId,
		onBlurLogonId,
		onSubmitInitialLogonId,
		onChangeOtpMethod,
		onSubmitOtpMethod,
		onChangeOtp,
		onToggleLoginPassword,
		onChangePassword,
		onSubmitLogin,
		onSubmitGoogleLogin,
		onToggleSelectOtpMethod,
		onToggleBackStep,
		loginBase: { passwordExpired, setPasswordExpired },
	} = useLoginLess();

	usePasswordExpiredRedirect(passwordExpired, setPasswordExpired, onClose);

	const currentExecuting = useRef(false);

	const handleSubmit = async () => {
		if (currentExecuting.current) {
			return;
		}

		currentExecuting.current = true;

		const response = await onSubmitLogin({});
		if ('userId' in (response ?? {})) {
			await mutate(personMutatorKeyMatcher(''), undefined);
			await showSuccessAuthLogin();
			!onAuthSuccess && (await onReplaceAuthRoute());
			!onAuthSuccess && (await onClose());
			await onAuthSuccess?.();
		}
		currentExecuting.current = false;
	};

	const handleSubmitOtpMethod = async () => {
		if (currentExecuting.current) {
			return;
		}

		currentExecuting.current = true;

		const response = await onSubmitOtpMethod({});
		if ('userId' in (response ?? {})) {
			await mutate(personMutatorKeyMatcher(''), undefined);
			await showSuccessAuthLogin();
			!onAuthSuccess && (await onReplaceAuthRoute());
			!onAuthSuccess && (await onClose());
			await onAuthSuccess?.();
		}
		currentExecuting.current = false;
	};

	const handleGoogleLogin = async (idToken: string) => {
		if (currentExecuting.current) {
			return;
		}

		currentExecuting.current = true;

		const response = await onSubmitGoogleLogin(idToken);
		if ('userId' in (response ?? {})) {
			await mutate(personMutatorKeyMatcher(''), undefined);
			await showSuccessAuthLogin();
			!onAuthSuccess && (await onReplaceAuthRoute());
			!onAuthSuccess && (await onClose());
			await onAuthSuccess?.();
		}
		currentExecuting.current = false;
	};

	const isFullContent = stateLogin.step === LOGIN_STEPS.INITIAL_LOGON_ID;

	return (
		<Drawer open={open} onClose={onClose} sx={{ zIndex: Z_INDEX.AUTH_FLOW_DRAWER }}>
			{action === AuthFlowAction.LOGIN ? (
				<Paper sx={DrawerLoginFrameSx}>
					<IconButton
						onClick={onClose}
						sx={{
							ml: 'auto',
						}}
					>
						<CloseIcon />
					</IconButton>
					<Stack
						sx={{
							width: '100%',
							flex: 1,
							padding: '0px 30px 40px 30px',
						}}
					>
						<SingleLoginLessTitle
							sx={DrawerLoginTitleSx}
							step={stateLogin.step}
							otpMethod={stateLogin.otpMethod}
							showPreviousMethodHint={stateLogin.showPreviousMethodHint}
							maskedPhone={stateLogin.maskedPhone}
							maskedEmail={stateLogin.maskedEmail}
							showInDialog
							origin={origin}
							onToggleBackStep={onToggleBackStep}
						/>
						<SingleLoginLessForm
							stateLogin={stateLogin}
							baseSettings={baseSettings}
							disabled={singleSettingsLoading || nextRouteLoading}
							onChangeLogonId={onChangeLogonId}
							onBlurLogonId={onBlurLogonId}
							onSubmitInitialLogonId={onSubmitInitialLogonId}
							onChangeOtpMethod={onChangeOtpMethod}
							onSubmitOtpMethod={handleSubmitOtpMethod}
							onChangeOtp={onChangeOtp}
							onChangePassword={onChangePassword}
							onSubmitLogin={handleSubmit}
							onToggleLoginPassword={onToggleLoginPassword}
							onToggleSelectOtpMethod={onToggleSelectOtpMethod}
							onGoogleLogin={handleGoogleLogin}
						/>
						{isFullContent ? (
							<>
								<Divider sx={{ marginY: '15px' }} />
								<Stack alignItems="center" spacing={'13px'}>
									<Typography sx={createAccountLabelSX}>{noAccountMsg}</Typography>
									<DpButton
										variant="text"
										sx={{
											fontSize: { xs: '18px', md: 'var(--font-size-sm)' },
											marginTop: '0px !important',
										}}
										onClick={() => onGo(AuthFlowAction.REGISTER)}
									>
										{sigIn}
									</DpButton>
								</Stack>
							</>
						) : null}
					</Stack>
				</Paper>
			) : (
				<Paper sx={DrawerLoginFrameSx}>
					<Stack direction="row" justifyContent="space-between" width="100%" mt="5px">
						<IconButton
							onClick={() => onGo(AuthFlowAction.LOGIN)}
							sx={{ ...ModalLoginHeaderIconCloseFrameSx, ml: '10px' }}
						>
							<ArrowBackIosIcon fontSize="medium" sx={ModalLoginHeaderIconCloseSx} />
						</IconButton>
						<IconButton onClick={onClose} sx={ModalLoginHeaderIconCloseFrameSx}>
							<CloseIcon fontSize="medium" sx={ModalLoginHeaderIconCloseSx} />
						</IconButton>
					</Stack>
					<Stack sx={DrawerRegisterContentSx}>
						<Register
							id="AuthFlowDrawer"
							withoutLogin
							shouldCallToModal
							origin={origin}
							onAuthSuccess={onAuthSuccess}
							onClose={onClose}
						/>
					</Stack>
				</Paper>
			)}
		</Drawer>
	);
};
